import arrayMutators from 'final-form-arrays';
import { HotelForm as Form } from 'hotel/HotelForm';
import { isEqual } from 'lodash';
import { useState } from 'react';

import { PrimaryButton, Button } from 'components/Button';
import OrderRequiredFields from 'components/OrderRequiredFields';
import {
  RequiredFieldInput,
  RosterRequestDataNewGroupReservation,
} from 'generated/graphql';
import { useClientRequiredFieldsAndPod } from 'hooks/useClientRequiredFieldsAndPod';
import { getOrderLevelFields } from 'utilities/guestFieldMapping';

interface GroupReservationRequiredFieldsProps {
  requestData: RosterRequestDataNewGroupReservation;
  onUpdateRequiredFields: (requiredFields: RequiredFieldInput[]) => void;
  loading?: boolean;
  hasBookings?: boolean;
}

interface FormValues {
  requiredFields: RequiredFieldInput[];
}

const GroupReservationRequiredFields = ({
  requestData,
  onUpdateRequiredFields,
  loading = false,
  hasBookings = false,
}: GroupReservationRequiredFieldsProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const { clientRequiredFields } = useClientRequiredFieldsAndPod();

  const onSubmit = (values: FormValues) => {
    onUpdateRequiredFields(values.requiredFields);
    setIsEditing(false);
  };

  const onCancel = () => {
    setIsEditing(false);
  };

  const existingRequiredFields = requestData.requiredFields || [];

  const orderLevelFieldTypes = getOrderLevelFields(
    clientRequiredFields.map((field) => field.value),
  );

  const initialValues: FormValues = {
    requiredFields: orderLevelFieldTypes.map((fieldType) => {
      const existingField = existingRequiredFields.find(
        (f) => f.name === fieldType,
      );
      return {
        name: fieldType,
        value: existingField?.value || '',
      };
    }),
  };

  if (!clientRequiredFields || clientRequiredFields.length === 0) {
    return null;
  }

  if (!orderLevelFieldTypes || orderLevelFieldTypes.length === 0) {
    return null;
  }

  return (
    <div className="mt-6">
      {hasBookings && (
        <div className="mb-4 rounded-md border border-yellow-200 bg-yellow-50 p-4">
          <div className="flex">
            <div className="shrink-0">
              <svg
                className="size-5 text-yellow-400"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                Required fields are locked because this roster has existing
                bookings.
              </h3>
            </div>
          </div>
        </div>
      )}
      {isEditing ? (
        <Form<FormValues>
          onSubmit={onSubmit}
          initialValues={initialValues}
          initialValuesEqual={isEqual}
          mutators={{ ...arrayMutators }}
          render={({ handleSubmit, pristine }) => (
            <form onSubmit={handleSubmit}>
              <div className="space-y-6">
                <OrderRequiredFields hideHeader={true} removeContainer={true} />

                <div className="flex flex-row gap-2">
                  <PrimaryButton
                    type="submit"
                    disabled={loading || pristine}
                    text={loading ? 'Saving...' : 'Save'}
                  />

                  <Button
                    type="button"
                    onClick={onCancel}
                    className="border border-black bg-transparent text-black hover:bg-gray-100"
                    text="Cancel"
                  />
                </div>
              </div>
            </form>
          )}
        />
      ) : (
        <div>
          <div className="space-y-4">
            {existingRequiredFields.filter((field) =>
              orderLevelFieldTypes.includes(field.name),
            ).length > 0 ? (
              existingRequiredFields
                .filter((field) => orderLevelFieldTypes.includes(field.name))
                .map((field) => {
                  const fieldConfig = clientRequiredFields.find(
                    (config) => config.value === field.name,
                  );
                  return (
                    <div key={field.name}>
                      <p className="mb-2 text-sm font-medium text-gray-500">
                        {fieldConfig?.label || field.name}
                      </p>
                      <p className="font-arimo text-base font-medium text-gray-900">
                        {field.value || '-'}
                      </p>
                    </div>
                  );
                })
            ) : (
              <p className="font-arimo text-base font-medium text-gray-900">
                No additional details required for this reservation.
              </p>
            )}
          </div>

          {!hasBookings && (
            <div className="mt-4 flex flex-row gap-2">
              <PrimaryButton onClick={() => setIsEditing(true)} text="Edit" />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GroupReservationRequiredFields;
